import {
  subWeeks,
  subMonths,
  subQuarters,
  subYears,
  format,
} from "date-fns";
import { getFYBounds } from "@/lib/date-utils";
import {
  getPeriodBounds as coreGetPeriodBounds,
  monthStart,
  monthEnd,
} from "@core";

export { monthStart, monthEnd };

/**
 * Pure period engine. Every bound is a `YYYY-MM-DD` string produced with
 * `format(_, "yyyy-MM-dd")` on a local `Date` — never a `Date` object and never
 * a UTC/`toISOString()` derivation, which would shift IST dates across a day
 * boundary. Zero I/O, zero Prisma.
 */

export type PeriodKey = "weekly" | "monthly" | "quarterly" | "halfyear" | "fy";

export interface PeriodBounds {
  start: string;
  end: string;
  label: string;
}

/** Local (never UTC) `YYYY-MM-DD` — `toISOString()` would shift IST dates. */
function toYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Reconstitute a local `Date` from a `YYYY-MM-DD` string for `date-fns` label
 * formatting only — never for arithmetic. The 3-arg `Date` constructor reads
 * its components as LOCAL time, unlike `new Date("YYYY-MM-DD")`, which reads
 * as UTC and is the exact parse that shifted IST dates by a day. */
function parseYmdLocal(s: string): Date {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
}

/** How many buckets a trend chart shows per period type when no count is given. */
const TREND_DEFAULTS: Record<PeriodKey, number> = {
  weekly: 8,
  monthly: 6,
  quarterly: 6,
  halfyear: 4,
  fy: 5,
};

/**
 * Indian FY quarters share calendar-quarter boundaries (Apr-Jun, Jul-Sep,
 * Oct-Dec, Jan-Mar) — only the label remaps: FY-Q1 is Apr-Jun, and Jan-Mar is
 * FY-Q4 of the FY that started the previous April.
 */
function fyQuarterLabel(ref: Date): string {
  const month = ref.getMonth(); // 0-indexed
  const quarter = month >= 3 ? Math.floor((month - 3) / 3) + 1 : 4;
  return `Q${quarter} ${getFYBounds(ref).label}`;
}

/**
 * Half-year has no native date-fns equivalent. H1 is Apr-Sep of the FY start
 * year; H2 is Oct-Mar and crosses the calendar year, so Jan-Mar belongs to the
 * PREVIOUS calendar year's H2. The bounds themselves come from `@core`'s
 * fiscal `"half"` period (string arithmetic); this only adds the label.
 */
function halfYearBounds(ref: Date): PeriodBounds {
  const month = ref.getMonth();
  const fyLabel = getFYBounds(ref).label;
  const { start, end } = coreGetPeriodBounds("half", toYmd(ref));
  const label = month >= 3 && month <= 8 ? `H1 ${fyLabel}` : `H2 ${fyLabel}`;
  return { start, end, label };
}

/** Bounds of the period containing `ref`. Delegates the actual math to
 * `@core` (string arithmetic, no `Date` object) and adapts it to this
 * module's `Date`-in / labelled-out call sites. */
export function getPeriodBounds(period: PeriodKey, ref: Date = new Date()): PeriodBounds {
  const refStr = toYmd(ref);
  switch (period) {
    case "weekly": {
      const { start, end } = coreGetPeriodBounds("week", refStr);
      return { start, end, label: `Week of ${format(parseYmdLocal(start), "d MMM")}` };
    }
    case "monthly": {
      const start = monthStart(refStr);
      const end = monthEnd(refStr);
      return { start, end, label: format(parseYmdLocal(start), "MMM yyyy") };
    }
    case "quarterly": {
      const { start, end } = coreGetPeriodBounds("quarterly", refStr);
      return { start, end, label: fyQuarterLabel(ref) };
    }
    case "halfyear":
      return halfYearBounds(ref);
    case "fy":
      return getFYBounds(ref);
  }
}

/** Move `ref` back by `n` whole periods of the given type. */
function shiftRef(period: PeriodKey, ref: Date, n: number): Date {
  switch (period) {
    case "weekly":
      return subWeeks(ref, n);
    case "monthly":
      return subMonths(ref, n);
    case "quarterly":
      return subQuarters(ref, n);
    case "halfyear":
      return subMonths(ref, n * 6);
    case "fy":
      return subYears(ref, n);
  }
}

/** The immediately-preceding period of the same type (drives period-over-period deltas). */
export function getPreviousPeriodBounds(
  period: PeriodKey,
  ref: Date = new Date(),
): PeriodBounds {
  return getPeriodBounds(period, shiftRef(period, ref, 1));
}

/**
 * `count` contiguous, non-overlapping buckets ending at the period containing
 * `ref`, returned oldest-first for direct use as a chart series.
 */
export function getTrendBuckets(
  period: PeriodKey,
  count: number = TREND_DEFAULTS[period],
  ref: Date = new Date(),
): PeriodBounds[] {
  const total = Math.max(1, Math.floor(count));
  const buckets: PeriodBounds[] = [];
  for (let i = total - 1; i >= 0; i--) {
    buckets.push(getPeriodBounds(period, shiftRef(period, ref, i)));
  }
  return buckets;
}
